import {
  appLogout,
  createSelector,
  createAsyncThunk,
  createSlice,
  injectSlice,
  PayloadAction,
  createAction,
} from '@bonanza-social/toolkit/state';

import QuestsService from '../services/QuestsService';
import { QuestsState } from './quests.types';
import { filterInGameQuests } from './quests.utils';
import { Quest, QuestStatus } from '../services/QuestsService.schemas';
import { generateSlug } from '@bonanza-social/toolkit/utils';

export const questNotFound = createAction('quests/questNotFound');

export const fetchQuest = createAsyncThunk(
  'quests/fetch-single',
  async (id: Quest['promotionId']) => {
    const rawQuest = await QuestsService.getQuest({
      pathParams: { promoId: id },
    });
    const quest: Quest = {
      ...rawQuest,
      slug: generateSlug(rawQuest.title),
    };

    return quest;
  },
);

export const fetchQuests = createAsyncThunk('quests/fetch', async () => {
  const res = await QuestsService.getQuests();

  const quests = res.inPeriodPromos.map((quest) => ({
    ...quest,
    slug: generateSlug(quest.title),
  }));

  return quests as Quest[];
});

export const activateQuest = createAsyncThunk(
  'quests/activate',
  async (id: Quest['promotionId']) => {
    const res = await QuestsService.getUserPromoOptinUrl({
      pathParams: { promoId: id },
    });

    return res;
  },
);

export const abandonQuest = createAsyncThunk(
  'quests/abandon',
  async (id: Quest['promotionId']) => {
    const res = await QuestsService.getUserPromoOptoutUrl({
      pathParams: { promoId: id },
    });

    return res;
  },
);

const initialState: QuestsState = {
  data: [],
  activatingQuestIds: [],
  abandoningQuestIds: [],
  dataState: 'idle',
  singleQuestDataState: 'idle',
};

const quests = createSlice({
  name: 'quests',
  initialState,
  reducers: {
    updateQuest(state, action: PayloadAction<Quest>) {
      const quest = state.data.find(
        (quest) => quest.promotionId === action.payload.promotionId,
      );

      if (quest) {
        Object.assign(quest, action.payload);
      }
    },
    removeQuest(state, { payload }: PayloadAction<number>) {
      state.data = state.data.filter((quest) => quest.promotionId !== payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchQuests.pending, (state) => {
      if (state.dataState === 'fulfilled') {
        state.dataState = 'refreshing';
      } else {
        state.dataState = 'pending';
      }
    });
    builder.addCase(fetchQuests.rejected, (state) => {
      state.dataState = 'rejected';
    });
    builder.addCase(fetchQuests.fulfilled, (state, action) => {
      state.dataState = 'fulfilled';
      state.data = action.payload;
    });
    builder.addCase(fetchQuest.fulfilled, (state, action) => {
      state.singleQuestDataState = 'fulfilled';
      state.data = state.data.map((quest: Quest) => {
        if (action.payload.promotionId === quest.promotionId) {
          return {
            ...quest,
            ...action.payload,
          };
        } else {
          return quest;
        }
      });
    });
    builder.addCase(fetchQuest.pending, (state) => {
      if (state.singleQuestDataState !== 'fulfilled') {
        state.singleQuestDataState = 'pending';
      }
    });
    builder.addCase(fetchQuest.rejected, (state) => {
      state.singleQuestDataState = 'rejected';
    });
    builder.addCase(activateQuest.pending, (state, action) => {
      state.activatingQuestIds.push(action.meta.arg);
    });
    builder.addCase(activateQuest.fulfilled, (state, action) => {
      const quest = state.data.find(
        (quest) => quest.promotionId === action.meta.arg,
      );

      if (quest) {
        quest.status = QuestStatus.OptIn;
        quest.isOptedIn = true;
      }
    });
    builder.addCase(abandonQuest.pending, (state, action) => {
      state.abandoningQuestIds.push(action.meta.arg);
    });
    builder.addCase(abandonQuest.fulfilled, (state, action) => {
      const quest = state.data.find(
        (quest) => quest.promotionId === action.meta.arg,
      );

      if (quest) {
        quest.status = QuestStatus.Available;
        quest.isOptedIn = false;
        quest.subTypeMeta.currentAccumulation = 0;
      }
    });
    builder.addCase(appLogout, () => {
      return initialState;
    });
    builder.addMatcher(activateQuest.settled, (state, action) => {
      state.activatingQuestIds = state.activatingQuestIds.filter(
        (id) => id !== action.meta.arg,
      );
    });
    builder.addMatcher(abandonQuest.settled, (state, action) => {
      state.abandoningQuestIds = state.abandoningQuestIds.filter(
        (id) => id !== action.meta.arg,
      );
    });
  },
});

export const { updateQuest, removeQuest } = quests.actions;

export const withQuests = injectSlice({
  reducerPath: 'quests',
  reducer: quests.reducer,
});

export const selectQuests = withQuests.selector((state) => state.quests.data);

export const selectQuest = createSelector(
  [(_, id: string | number) => id, selectQuests],
  (id, quests) =>
    quests.find((quest) => quest.slug === id || quest.promotionId == id),
);

export const selectActiveQuestsForGame = createSelector(
  [(_, gameId: number) => gameId, selectQuests],
  (gameId, quests) => filterInGameQuests(quests, gameId),
);

export const selectQuestsDataState = withQuests.selector(
  (state) => state.quests.dataState,
);

export const selectActivatingQuestIds = withQuests.selector(
  (state) => state.quests.activatingQuestIds,
);

export const selectIsActivatingQuest = (id?: Quest['promotionId']) =>
  createSelector(selectActivatingQuestIds, (ids) =>
    id ? ids.indexOf(id) !== -1 : false,
  );

export const selectAbandoningQuestIds = withQuests.selector(
  (state) => state.quests.abandoningQuestIds,
);

export const selectIsAbandoningQuest = (id?: Quest['promotionId']) =>
  createSelector(selectAbandoningQuestIds, (ids) =>
    id ? ids.indexOf(id) !== -1 : false,
  );
declare global {
  interface LazyLoadedSlices {
    quests: ReturnType<typeof quests.reducer>;
  }
}
